/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { IImageData, Image, IRequestContext } from '@msdyn365-commerce/core';
import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import React from 'react';

/**
 * @type AreaEvent for active points
 */
type AreaEvent = Event | React.MouseEvent<HTMLAreaElement, MouseEvent> | React.FocusEvent<HTMLAreaElement>;

/**
 * @interface IRgbColor
 * @description - The IRgbColor interface contract.
 */
export interface IRgbColor {
    rValue: number;
    gValue: number;
    bValue: number;
}

/**
 * @interface IArea
 * @description - The IArea interface contract.
 */
export interface IArea {
    areaId: string;
    coords: number[];
    href?: string;
    center?: number[];
    primaryImageUrl?: string;
    productUrl: string;
    productSearchResult?: ProductSearchResult;
}

/**
 * @interface IArea
 * @description - The IArea interface contract.
 */
export interface IAreaMap {
    name: string;
    areas: IArea[];
}

/**
 * @enum CanvasCalculations
 * @description - The canvas calculations enum.
 */
export enum CanvasCalculations {
    defaultWidth = 0,
    defaultHeight = 0,
    defaultPositionX = 0,
    defaultPositionY = 0,
    defaultScale = 1,
    halfDivisor = 2,
    doubleMultiplier = 2,
    buttonHeight = 30,
    buttonPadding = 30,
    paddingAdjustment = 3,
    expandAnimationStartWidth = 10,
    expandAnimationEndWidth = 40,
    collapseAnimationStartWidth = 40,
    collapseAnimationEndWidth = 8,
    expandRadius = 12,
    collapseRadius = 8,
    rotation = 1.57,
    startAngle = 0,
    endAngle = 7,
    clearMargin = 50,
    clearHeight = 100,
    clearWidth = 100,
    textFadeInStartValue = 0,
    textFadeOutStartValue = 0.5,
    textFadeInterval = 0.01
}

/**
 * @enum CanvasStyles
 * @description - The canvas styles enum.
 */
export enum CanvasStyles {
    color = '#FFFFFF',
    buttonTextClor = '#000000'
}

/**
 * @enum
 * @description - The canvas styles enum.
 */
export interface IImageMapperResources {
    buttonText?: string;
    altText?: string;
}

/**
 * @interface IImageMapperProps
 * @description - The IImageMapperProps interface contract.
 */
export interface IImageMapperProps {
    imageData: IImageData;
    areaMap: IAreaMap;
    viewPort: string;
    requestContext: IRequestContext;
    color?: string;
    buttonTextClor?: string;
    width?: number;
    imgWidth?: number;
    activeImageRefs: React.RefObject<HTMLDivElement>;
    imageMapperResources?: IImageMapperResources;
    onLoad?(): void;
    onClick?(area: IArea, index: number, event: AreaEvent): void;
    onFocus?(area: IArea, index: number, event: AreaEvent): void;
    onMouseEnter?(area: IArea, index: number, event: AreaEvent): void;
    onMouseLeave?(area: IArea, index: number, event: AreaEvent): void;
}

/**
 * ImageMapper.
 */
export class ImageMapper extends React.Component<IImageMapperProps> {
    private readonly _backgroundImage: React.RefObject<Image>;

    private readonly _container: React.RefObject<HTMLDivElement>;

    private readonly _canvas: React.RefObject<HTMLCanvasElement>;

    private _isDesktopViewPort: boolean = false;

    private _context: CanvasRenderingContext2D | null | undefined = null;

    public constructor(props: IImageMapperProps) {
        super(props);
        this._backgroundImage = React.createRef();
        this._container = React.createRef();
        this._canvas = React.createRef();
    }

    /**
     * ComponentDidMount method.
     */
    public componentDidMount(): void {
        const { viewPort } = this.props;
        this.initCanvas();
        this._isDesktopViewPort = viewPort === 'lg' || viewPort === 'xl';
        this.renderAreas();
        this._context = null;
    }

    /**
     * ComponentDidUpdate method.
     */
    public componentDidUpdate(): void {
        const { viewPort } = this.props;
        this.initCanvas();
        this._isDesktopViewPort = viewPort === 'lg' || viewPort === 'xl';
    }

    /**
     * ShouldComponentUpdate method.
     * @param nextProps - Next props.
     * @returns Returns boolean.
     */
    public shouldComponentUpdate(nextProps: IImageMapperProps): boolean {
        let shouldRender = false;
        if (nextProps !== this.props) {
            shouldRender = true;
        }
        return shouldRender;
    }

    /**
     * GetActiveImageWidth method - Gets the active image width.
     * @returns Returns number | undefined.
     */
    public getActiveImageWidth = (): number | undefined => {
        if (MsDyn365.isBrowser) {
            const activeImageReferenceValue = this.props.activeImageRefs;
            if (activeImageReferenceValue.current !== null) {
                return activeImageReferenceValue.current.clientWidth;
            }
        }
        return undefined;
    };

    /**
     * GetActiveImageHeight method - Gets the active image height.
     * @returns Returns number | undefined.
     */
    public getActiveImageHeight = (): number | undefined => {
        if (MsDyn365.isBrowser) {
            const activeImageReferenceValue = this.props.activeImageRefs;
            if (activeImageReferenceValue.current !== null) {
                return activeImageReferenceValue.current.clientHeight;
            }
        }
        return undefined;
    };

    /**
     * ConvertHexToRgb method - This method takes color in HEX format and returns IRgbColor color object.
     * @param hexColor -The color hex value.
     * @returns -Returns IRgbColor object.
     */
    public convertHexToRgb = (hexColor: string): IRgbColor => {
        // RegEx used to execute the conversion of the hex color to rgb value.
        const regEx = new RegExp('^#?([a-f\\d]{2})([a-f\\d]{2})([a-f\\d]{2})$', 'u');

        // RegEx.exec method executes a search for a match in a specified string. Returns a result array, or null.
        const result = regEx.exec(hexColor);
        const defaultValue = 0;
        return result
            ? {
                  rValue: Number.parseInt(result[1], 16),
                  gValue: Number.parseInt(result[2], 16),
                  bValue: Number.parseInt(result[3], 16)
              }
            : {
                  rValue: defaultValue,
                  gValue: defaultValue,
                  bValue: defaultValue
              };
    };

    /**
     * ApplyFadingTransition method - Applys fading transition to button text.
     * @param text -The button text.
     * @param coords -The coordinates.
     * @param rgbColor -The RGB color object.
     * @param alpha -The alpha value of the button text.
     */
    public applyFadingTransition = (text: string, coords: number[], rgbColor: IRgbColor, alpha: number): void => {
        const halfDivisor: number = CanvasCalculations.halfDivisor;
        const context = this._canvas.current?.getContext('2d') ?? this._context;
        if (context && this._isDesktopViewPort) {
            const buttonPadding = CanvasCalculations.buttonPadding;
            const paddingAdjustment = CanvasCalculations.paddingAdjustment;
            const buttonWidth = context.measureText(text).width + buttonPadding;
            context.fillStyle = `rgba(${rgbColor.rValue}, ${rgbColor.gValue}, ${rgbColor.bValue}, ${alpha} )`;
            if (MsDyn365.isBrowser && document.body.parentElement && document.body.parentElement.getAttribute('dir') === 'rtl') {
                context.fillText(text, coords[0] + buttonWidth / halfDivisor - buttonPadding / halfDivisor, coords[1] + paddingAdjustment);
            } else {
                context.fillText(text, coords[0] - buttonWidth / halfDivisor + buttonPadding / halfDivisor, coords[1] + paddingAdjustment);
            }
        }
    };

    /**
     * Draw button method - Draws button and contains fixed hight, padding values.
     * @param text -The button text.
     * @param coords -The coordinates.
     */
    public drawButton = (text: string, coords: number[]): void => {
        const { color = CanvasStyles.color, buttonTextClor = CanvasStyles.buttonTextClor } = this.props;
        const halfDivisor: number = CanvasCalculations.halfDivisor;
        const context = this._canvas.current?.getContext('2d') ?? this._context;
        if (context && this._isDesktopViewPort) {
            const buttonHeight = CanvasCalculations.buttonHeight;
            const buttonPadding = CanvasCalculations.buttonPadding;
            const paddingAdjustment = CanvasCalculations.paddingAdjustment;
            const buttonWidth = context.measureText(text).width + buttonPadding;
            const buttonPositionX = coords[0] - buttonWidth / halfDivisor;
            const buttonPositionY = coords[1] - buttonHeight / halfDivisor;
            const arcsize = buttonHeight / halfDivisor;

            context.beginPath();
            context.moveTo(buttonPositionX + arcsize, buttonPositionY);
            context.lineTo(buttonPositionX + buttonWidth - arcsize, buttonPositionY);
            context.arcTo(
                buttonPositionX + buttonWidth,
                buttonPositionY,
                buttonPositionX + buttonWidth,
                buttonPositionY + arcsize,
                arcsize
            );
            context.lineTo(buttonPositionX + buttonWidth, buttonPositionY + buttonHeight - arcsize);
            context.arcTo(
                buttonPositionX + buttonWidth,
                buttonPositionY + buttonHeight,
                buttonPositionX + buttonWidth - arcsize,
                buttonPositionY + buttonHeight,
                arcsize
            );
            context.lineTo(buttonPositionX + arcsize, buttonPositionY + buttonHeight);
            context.arcTo(buttonPositionX, buttonPositionY + buttonHeight, buttonPositionX, buttonPositionY - arcsize, arcsize);
            context.lineTo(buttonPositionX, buttonPositionY + arcsize);
            context.arcTo(buttonPositionX, buttonPositionY, buttonPositionX + arcsize, buttonPositionY, arcsize);
            context.lineTo(buttonPositionX + arcsize, buttonPositionY);
            context.stroke();
            context.closePath();
            context.fillStyle = color;
            context.fill();
            context.fillStyle = buttonTextClor;
            if (MsDyn365.isBrowser && document.body.parentElement && document.body.parentElement.getAttribute('dir') === 'rtl') {
                context.fillText(text, coords[0] + buttonWidth / halfDivisor - buttonPadding / halfDivisor, coords[1] + paddingAdjustment);
            } else {
                context.fillText(text, coords[0] - buttonWidth / halfDivisor + buttonPadding / halfDivisor, coords[1] + paddingAdjustment);
            }
        }
    };

    /**
     * ExpandAnimation method - Shows expand animation and contains fixed values to draw the circle and ellipse shapes.
     * @param coords -Coordinates.
     */
    public expandAnimation = (coords: number[]): void => {
        const { color = CanvasStyles.color, buttonTextClor = CanvasStyles.buttonTextClor } = this.props;
        const buttonText = this.props.imageMapperResources?.buttonText;
        let animationStartWidth = CanvasCalculations.expandAnimationStartWidth;
        const pointPositionX = coords[0];
        const pointPositionY = coords[1];
        const animationEndWidth = CanvasCalculations.expandAnimationEndWidth;
        const radius = CanvasCalculations.expandRadius;
        const rotation = CanvasCalculations.rotation;
        const startAngle = CanvasCalculations.startAngle;
        const endAngle = CanvasCalculations.endAngle;
        let textFadeInValue = CanvasCalculations.textFadeInStartValue;
        const buttonTextClorInRgb = this.convertHexToRgb(buttonTextClor.toLocaleLowerCase());

        /**
         * Animate method - Reders expand animation.
         */
        const animate = () => {
            const context = this._canvas.current?.getContext('2d') ?? this._context;
            if (!context) {
                return;
            }
            context.beginPath();
            context.ellipse(pointPositionX, pointPositionY, radius, animationStartWidth, rotation, startAngle, endAngle);
            context.fillStyle = color;
            context.fill();
            context.fillStyle = color;
            if (buttonText) {
                if (animationStartWidth < animationEndWidth) {
                    requestAnimationFrame(animate);
                    this.applyFadingTransition(buttonText, coords, buttonTextClorInRgb, textFadeInValue);
                    textFadeInValue += CanvasCalculations.textFadeInterval;
                    animationStartWidth++;
                } else {
                    this.drawButton(buttonText, [pointPositionX, pointPositionY]);
                }
            }
        };

        if (this._isDesktopViewPort) {
            animate();
        }
    };

    /**
     * CollapseAnimation method - Shows collapse animation and contains fixed values to draw the circle and ellipse shapes.
     * @param coords -Coordinates.
     */
    public collapseAnimation = (coords: number[]): void => {
        const { color = CanvasStyles.color, buttonTextClor = CanvasStyles.buttonTextClor } = this.props;
        const buttonText = this.props.imageMapperResources?.buttonText;
        let animationStartWidth = CanvasCalculations.collapseAnimationStartWidth;
        const pointPositionX = coords[0];
        const pointPositionY = coords[1];
        const animationEndWidth = CanvasCalculations.collapseAnimationEndWidth;
        const rotation = CanvasCalculations.rotation;
        const clearMargin = CanvasCalculations.clearMargin;
        const clearHeight = CanvasCalculations.clearHeight;
        const clearWidth = CanvasCalculations.clearWidth;
        const radius = CanvasCalculations.collapseRadius;
        const startAngle = CanvasCalculations.startAngle;
        const endAngle = CanvasCalculations.endAngle;
        let textFadeOutValue = CanvasCalculations.textFadeOutStartValue;
        const buttonTextClorInRgb = this.convertHexToRgb(buttonTextClor.toLocaleLowerCase());

        /**
         * Animate method - Renders collapse animation.
         */
        const animate = () => {
            const context = this._canvas.current?.getContext('2d') ?? this._context;
            if (!context) {
                return;
            }
            context.beginPath();
            context.clearRect(pointPositionX - clearMargin, pointPositionY - clearMargin, clearWidth, clearHeight);
            context.ellipse(pointPositionX, pointPositionY, radius, animationStartWidth, rotation, startAngle, endAngle);
            context.fillStyle = color;
            context.fill();
            if (animationEndWidth < animationStartWidth && buttonText) {
                requestAnimationFrame(animate);
                this.applyFadingTransition(buttonText, coords, buttonTextClorInRgb, textFadeOutValue);
                textFadeOutValue -= CanvasCalculations.textFadeInterval;
                animationStartWidth--;
            }
        };

        if (this._isDesktopViewPort) {
            animate();
        }
    };

    /**
     * DrawCircle method - Renders active points.
     * @param coords -Coordinates.
     */
    public drawCircle = (coords: number[]): void => {
        const { color = CanvasStyles.color, viewPort } = this.props;
        const doubleMultiplier: number = CanvasCalculations.doubleMultiplier;
        const startAngle: number = CanvasCalculations.startAngle;
        const context = this._canvas.current?.getContext('2d') ?? this._context;
        const radius = viewPort === 'xs' || viewPort === 'sm' ? coords[2] * doubleMultiplier : coords[2];
        if (!context) {
            return;
        }
        context.fillStyle = color;
        context.beginPath();
        context.strokeStyle = color;
        context.arc(coords[0], coords[1], radius, startAngle, Math.PI * doubleMultiplier);
        context.closePath();
        context.stroke();
        context.fill();
    };

    /**
     * ScaleCoords method - To scale the active point coordinates based on the window size.
     * @param coords -Coordinates.
     * @returns -Returns number[].
     */
    public scaleCoords = (coords: number[]): number[] => {
        const { imgWidth } = this.props;
        const defaultWidth: number = CanvasCalculations.defaultWidth;
        const defaultScale: number = CanvasCalculations.defaultScale;

        // Calculate scale based on current 'width' and the original 'imgWidth'
        const width = this.getActiveImageWidth();
        const scale = width && imgWidth && imgWidth > defaultWidth ? width / imgWidth : defaultScale;
        return coords.map(coord => coord * scale);
    };

    /**
     * RenderPrefilledAreas method - To render active points in case of events.
     */
    public renderPrefilledAreas = (): void => {
        const { areaMap } = this.props;
        areaMap.areas.map(area => {
            this.drawCircle(this.scaleCoords(area.coords));
            return null;
        });
    };

    /**
     * ComputeCenter method - To re-calculate the center of active point based on window size.
     * @param area -IArea.
     * @returns -Returns number[].
     */
    public computeCenter = (area: IArea): number[] => {
        const scaledCoords = this.scaleCoords(area.coords);
        return [scaledCoords[0], scaledCoords[1]];
    };

    /**
     * HandleHoverOn method - To handle mouse hover on event.
     * @param area -IArea.
     */
    public handleHoverOn = (area: IArea): void => {
        if (area.center && this._canvas.current) {
            this.drawCircle(this.scaleCoords(area.coords));
            this.expandAnimation(area.center);
        }
    };

    /**
     * HandleHoverOff method - To handle mouse hover off event.
     * @param area -IArea.
     */
    public handleHoverOff = (area: IArea): void => {
        if (this._canvas.current) {
            const context = this._canvas.current.getContext('2d') ?? this._context;
            const canvasPositionX: number = CanvasCalculations.defaultPositionX;
            const canvasPositionY: number = CanvasCalculations.defaultPositionY;

            if (!context) {
                return;
            }
            context.clearRect(canvasPositionX, canvasPositionY, this._canvas.current.width, this._canvas.current.height);
            this.renderPrefilledAreas();
            if (area.center) {
                this.collapseAnimation(area.center);
            }
        }
    };

    /**
     * Click method - Mouse click event.
     * @param area -IArea.
     * @param index - Number.
     * @returns - Void.
     */
    public click = (area: IArea, index: number) => (event: AreaEvent): void => {
        const { onClick } = this.props;
        if (onClick) {
            event.preventDefault();
            onClick(area, index, event);
        }
    };

    /**
     * HoverOn method - Mouse hover on event.
     * @param area -IArea.
     * @param index - Number.
     * @returns - Void.
     */
    public hoverOn = (area: IArea, index: number) => (event: AreaEvent): void => {
        const { onMouseEnter } = this.props;
        this.handleHoverOn(area);
        if (onMouseEnter) {
            onMouseEnter(area, index, event);
        }
    };

    /**
     * HoverOff method - Mouse hover off event.
     * @param area - IArea.
     * @param index - Number.
     * @returns - Void.
     */
    public hoverOff = (area: IArea, index: number) => (event: AreaEvent): void => {
        const { onMouseLeave } = this.props;

        this.handleHoverOff(area);

        if (onMouseLeave) {
            onMouseLeave(area, index, event);
        }
    };

    /**
     * RenderAreas method - Renders active points at given coordinates.
     * @returns -Returns JSX.Element[].
     */
    public renderAreas = (): JSX.Element[] => {
        const { areaMap, viewPort } = this.props;
        const buttonText = this.props.imageMapperResources?.buttonText ? this.props.imageMapperResources.buttonText : '';
        const altText = this.props.imageMapperResources?.altText ? this.props.imageMapperResources.altText : '';
        const doubleMultiplier: number = CanvasCalculations.doubleMultiplier;

        return areaMap.areas.map((area, index) => {
            const scaledCoords = this.scaleCoords(area.coords);
            const xsRadius = area.coords[2] * doubleMultiplier;
            if (viewPort === 'xs') {
                scaledCoords[2] = xsRadius;
            }
            const center = this.computeCenter(area);
            const extendedArea = { ...area, scaledCoords, center };
            const areaKey = `area-${index}`;
            const altTextWithProductName = altText
                .replace('{buttonText}', buttonText ? buttonText : '')
                .replace('{productName}', area.productSearchResult?.Name ? area.productSearchResult.Name : '');
            return (
                <area
                    key={areaKey}
                    shape='circle'
                    coords={scaledCoords.join(',')}
                    onMouseEnter={this.hoverOn(extendedArea, index)}
                    onMouseLeave={this.hoverOff(extendedArea, index)}
                    onClick={this.click(extendedArea, index)}
                    onFocus={this.hoverOn(extendedArea, index)}
                    onBlur={this.hoverOff(extendedArea, index)}
                    href={area.productUrl}
                    tabIndex={0}
                    alt={altTextWithProductName}
                    aria-label={altTextWithProductName}
                />
            );
        });
    };

    /**
     * InitCanvas method - To render the canvas.
     */
    public initCanvas = (): void => {
        if (!this._canvas.current || !this._backgroundImage.current || !this._container.current) {
            return;
        }

        const { color, onLoad } = this.props;
        const defaultWidth: number = CanvasCalculations.defaultWidth;
        const defaultHeight: number = CanvasCalculations.defaultHeight;

        const canvasWidth = this.getActiveImageWidth() ?? defaultWidth;
        const canvasHeight = this.getActiveImageHeight() ?? defaultHeight;

        this._canvas.current.width = canvasWidth;
        this._canvas.current.height = canvasHeight;
        this._container.current.style.width = `${canvasWidth}px`;

        const context = this._canvas.current.getContext('2d') ?? this._context;
        if (!context) {
            return;
        }
        context.fillStyle = color ?? '';

        if (onLoad) {
            onLoad();
        }

        this.renderPrefilledAreas();
    };

    /**
     * Render method - To render the image mapper component with active image, canvas and active points.
     * @returns -Returns JSX.Element | null.
     */
    public render(): JSX.Element | null {
        const { imageData, areaMap, requestContext } = this.props;

        return (
            <div className='msc-active-image-container' ref={this._container}>
                <Image
                    src={imageData.src}
                    useMap={`#${areaMap.name}`}
                    alt={areaMap.name}
                    ref={this._backgroundImage}
                    height={imageData.height}
                    width={imageData.width}
                    className='msc-active-image-img'
                    imageSettings={imageData.imageSettings}
                    gridSettings={requestContext.gridSettings ?? {}}
                    requestContext={requestContext}
                />
                <canvas ref={this._canvas} className='msc-active-image-canvas' />
                <map name={areaMap.name} className='msc-active-image-map'>
                    {this.renderAreas()}
                </map>
            </div>
        );
    }
}

export default ImageMapper;
